import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

import Paywall from "@/components/Paywall";
import { Avatar } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { upload } from "@/services/api/Career";
import { addTeamMember, updateTeamMember } from "@/services/api/Startup";
import { avatarOptions, getAvatarByIdOrDefault } from "@/utils/avatarOptions";
import { Images, Pencil, Plus } from "@phosphor-icons/react";
import { Check } from "@phosphor-icons/react/dist/ssr";
import Image from "next/legacy/image";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";

export const EditTeam = ({
  startup,
  member,
  planSubscription,
  user,
  hasAccess,
  action,
}) => {
  const [imgSrc, setImgSrc] = useState("");
  const [loadingUpload, setLoadingUpload] = useState(false);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [linkedin, setLinkedin] = useState("");
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState("");
  const [isFounder, setIsFounder] = useState(false);
  const [isCofounder, setIsCofounder] = useState(false);

  const handleChange = (e) => {
    switch (e.target.name) {
      case "name":
        setName(e.target.value);
        break;
      case "title":
        setTitle(e.target.value);
        break;
      case "linkedin":
        setLinkedin(e.target.value);
        break;
    }
  };

  useEffect(() => {
    if (member) {
      setName(member.name);
      setTitle(member.position);
      setImgSrc(member.photo_url);
      setLinkedin(member.linkedin_url);
      setSelectedAvatar(member.avatar || "");
      setIsFounder(member.is_founder || false);
      setIsCofounder(member.is_cofounder || false);
    }
  }, [member]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append("filepond", file);
    formData.append("folder", "_startups/user_uploads/team_members");

    setLoadingUpload(true);
    try {
      const response = await upload(formData);

      setImgSrc(response.data.url);
      setSelectedAvatar("");
    } catch (err) {
      console.error(err);
    }
    setLoadingUpload(false);
  };

  const handleRemoveImage = () => {
    setImgSrc("");
    // Reset the file input value so the same file can be selected again
    const fileInput = document.getElementById("hiddenFileInput");
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleRemoveAvatar = () => {
    setSelectedAvatar("");
  };

  const handleRoleChange = (value) => {
    switch (value) {
      case "founder":
        setIsFounder(true);
        setIsCofounder(false);
        break;
      case "cofounder":
        setIsFounder(false);
        setIsCofounder(true);
        break;
      case "other":
        setIsFounder(false);
        setIsCofounder(false);
        break;
    }
  };

  const handleSubmit = async () => {
    if (!name) return toast.error("Name is required!");
    if (!title) return toast.error("Title is required!");

    const payload = {
      startup_id: startup.id,
      name,
      position: title,
      photo_url: imgSrc,
      linkedin_url: linkedin,
      avatar: selectedAvatar,
      is_founder: isFounder,
      is_cofounder: isCofounder,
    };

    try {
      if (member) {
        await updateTeamMember(member.id, payload);
        toast.success("Successfully update team member!");
      } else {
        await addTeamMember(payload);
        toast.success("Successfully add team member!");
      }

      setTimeout(() => {
        if (action) {
          action();
          setOpen(false);
          setName("");
          setTitle("");
          setImgSrc("");
          setLinkedin("");
        } else {
          router.reload();
        }
      }, 200);
    } catch (error) {}
  };

  if (
    !hasAccess &&
    !(
      (planSubscription && planSubscription.is_startup_plan) ||
      user?.roles == "admin-team" ||
      user?.roles == "admin"
    )
  ) {
    return (
      <Paywall
        title={
          "Upgrade to the Startup Registration plan to edit your startup profile"
        }
        description={`Only users with the Startup Registration plan can edit and publish the startup profile.`}
        startup={startup}
      >
        <Button
          size='icon'
          className='relative w-10 h-10 text-xl text-white rounded-full bg-gradient-to-tr from-yellow-1 to-red-1'
        >
          <span className='absolute inline-flex w-full h-full rounded-full animate-ping bg-orange-1/50'></span>
          {member ? <Pencil weight='fill' /> : <Plus />}
        </Button>
      </Paywall>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          size='icon'
          className='relative w-10 h-10 text-xl text-white rounded-full bg-gradient-to-tr from-yellow-1 to-red-1'
        >
          <span className='absolute inline-flex w-full h-full rounded-full animate-ping bg-orange-1/50'></span>

          {member ? <Pencil weight='fill' /> : <Plus />}
        </Button>
        {/* <Button
          variant='outline'
          size='icon'
          className='w-12 h-12 rounded-full bg-white/10'
        >
          {member ? <Pen className='text-xl' /> : <Plus className='text-xl' />}
        </Button> */}
      </DialogTrigger>
      <DialogContent className='bg-white text-background'>
        <DialogHeader>
          <DialogTitle>Add Team Member</DialogTitle>
          <DialogDescription>
            <div className='grid flex-1 grid-cols-12 mt-5 gap-x-3 gap-y-4'>
              <div className='w-full col-span-12 space-y-1.5'>
                <div className='flex items-start space-x-5'>
                  <div className='relative flex-shrink-0 overflow-hidden border rounded-md w-28 border-background/20 aspect-square'>
                    {!loadingUpload && (imgSrc || selectedAvatar) ? (
                      <Image
                        src={imgSrc || getAvatarByIdOrDefault(selectedAvatar)}
                        layout={"fill"}
                        objectFit={"cover"}
                        alt='Image'
                      />
                    ) : (
                      <div className='flex items-center justify-center w-full h-full text-3xl bg-background/5'>
                        <Images className='text-background' />
                      </div>
                    )}
                  </div>
                  <input
                    type='file'
                    onChange={handleImageUpload}
                    accept='image/*'
                    className='hidden'
                    id='hiddenFileInput'
                  />
                  <div>
                    {!imgSrc && (
                      <>
                        <span className='text-xs font-medium text-background mb-1.5 block'>
                          Choose Avatar
                        </span>

                        <div className='flex flex-wrap gap-0.5'>
                          {avatarOptions.map((avatar) => (
                            <div
                              key={avatar.id}
                              className={`relative border-2 rounded-full cursor-pointer ${
                                selectedAvatar === avatar.id
                                  ? "border-black"
                                  : "border-transparent"
                              }`}
                              onClick={() => {
                                setSelectedAvatar(avatar.id);
                                setImgSrc("");
                              }}
                            >
                              {selectedAvatar === avatar.id && (
                                <Check
                                  weight='bold'
                                  className='absolute z-10 text-xl text-black -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2'
                                />
                              )}
                              <Avatar
                                className={
                                  selectedAvatar === avatar.id
                                    ? "opacity-30"
                                    : ""
                                }
                              >
                                <Image src={avatar.src} alt={avatar.label} />
                              </Avatar>
                            </div>
                          ))}
                        </div>
                      </>
                    )}

                    {!selectedAvatar && <p className='mt-2 mb-1 text-xs text-background/80'>
                      {imgSrc
                        ? "Change or remove image"
                        : "Or upload your own image"}
                    </p>}
                    <div className='flex gap-2'>
                      {!imgSrc && !selectedAvatar && (
                        <>
                          <Button
                            disabled={loadingUpload}
                            className='w-auto h-auto px-3 py-2 text-xs text-white bg-background hover:bg-zinc-800'
                            onClick={() =>
                              document.getElementById("hiddenFileInput").click()
                            }
                          >
                            {loadingUpload
                              ? "Uploading..."
                              : "Upload Image"}
                          </Button>
                        </>
                      )}

                      {imgSrc && (
                        <Button
                          variant='outline'
                          className='w-auto h-auto px-3 py-2 text-xs text-red-500 bg-white border-red-500 hover:bg-red-50 hover:text-red-600'
                          onClick={handleRemoveImage}
                        >
                          Remove Image
                        </Button>
                      )}

                      {selectedAvatar && !imgSrc && (
                        <Button
                          variant='outline'
                          className='w-auto h-auto px-3 py-2 text-xs text-red-500 bg-white border-red-500 hover:bg-red-50 hover:text-red-600 mt-2'
                          onClick={handleRemoveAvatar}
                        >
                          Remove Avatar
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className='w-full col-span-12 space-y-1.5'>
                <Label
                  className='text-xs text-background'
                  htmlFor='member_name'
                >
                  Name
                </Label>
                <Input
                  name='name'
                  id='member_name'
                  className='bg-white text-background border-background/20 focus-visible:ring-0'
                  value={name}
                  onChange={handleChange}
                  placeholder='Full Name'
                />
              </div>
              <div className='w-full col-span-12 space-y-1.5'>
                <RadioGroup
                  defaultValue='other'
                  value={
                    isFounder ? "founder" : isCofounder ? "cofounder" : "other"
                  }
                  onValueChange={handleRoleChange}
                  className='flex gap-4'
                >
                  <div className='flex items-center space-x-2'>
                    <RadioGroupItem
                      value='founder'
                      id='founder'
                      className='border-background/30 data-[state=checked]:bg-background'
                    />
                    <Label
                      htmlFor='founder'
                      className='text-sm text-background'
                    >
                      Founder
                    </Label>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <RadioGroupItem
                      value='cofounder'
                      id='cofounder'
                      className='border-background/30 data-[state=checked]:bg-background'
                    />
                    <Label
                      htmlFor='cofounder'
                      className='text-sm text-background'
                    >
                      Co-Founder
                    </Label>
                  </div>
                  <div className='flex items-center space-x-2'>
                    <RadioGroupItem
                      value='other'
                      id='other'
                      className='border-background/30 data-[state=checked]:bg-background'
                    />
                    <Label htmlFor='other' className='text-sm text-background'>
                      Other
                    </Label>
                  </div>
                </RadioGroup>
              </div>
              <div className='w-full col-span-12 space-y-1.5'>
                <Label className='text-xs text-background' htmlFor='job_title'>
                  Title
                </Label>
                <Input
                  name='title'
                  id='job_title'
                  className='bg-white text-background border-background/20 focus-visible:ring-0'
                  value={title}
                  onChange={handleChange}
                  placeholder='CEO'
                />
              </div>

              <div className='w-full col-span-12 space-y-1.5'>
                <Label
                  className='text-xs text-background'
                  htmlFor='linkedin_url'
                >
                  LinkedIn URL
                </Label>
                <Input
                  value={linkedin}
                  onChange={handleChange}
                  name='linkedin'
                  type='text'
                  id='linkedin_url'
                  className='bg-white text-background border-background/20 focus-visible:ring-0'
                  placeholder='https://www.linkedin.com/in/xxxxxxxxxxx'
                />
              </div>
              <Button
                size={"lg"}
                className='col-span-12 font-medium'
                onClick={handleSubmit}
                variant='secondary'
              >
                {member ? "Save Changes" : "Add Member"}
              </Button>
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  );
};
